// disable useContraMutation eslint error

import {
  type RegisterVisitorPortfolioProjectViewEventInput,
  type RegisterVisitorProductizedServiceViewEventInput,
  type RegisterVisitorUserProfileViewEventInput,
} from '#app/__generated__/api.js';
import { type useSendAnalyticEventMutationPortfolioProjectViewEventMutation } from '#app/__generated__/relay/useSendAnalyticEventMutationPortfolioProjectViewEventMutation.graphql.js';
import { type useSendAnalyticEventMutationProductizedServiceViewEventMutation } from '#app/__generated__/relay/useSendAnalyticEventMutationProductizedServiceViewEventMutation.graphql.js';
import { type useSendAnalyticEventMutationProfileViewEventMutation } from '#app/__generated__/relay/useSendAnalyticEventMutationProfileViewEventMutation.graphql.js';
import { useFirstReferrerUrl } from '#app/hooks/useFirstReferrerUrl.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { graphql, useMutation } from '@contra/relay';
import { useCallback, useMemo } from 'react';

export type RegisterEventAction =
  | RegisterPortfolioProjectViewEventAction
  | RegisterProductizedServiceViewEventAction
  | RegisterUserProfileViewEventAction;

type RegisterPortfolioProjectViewEventAction = {
  eventType: 'PortfolioProjectView';
  input: Omit<
    RegisterVisitorPortfolioProjectViewEventInput,
    'currentUrl' | 'referrerUrl'
  >;
};

type RegisterProductizedServiceViewEventAction = {
  eventType: 'ProductizedServiceView';
  input: Omit<
    RegisterVisitorProductizedServiceViewEventInput,
    'currentUrl' | 'referrerUrl'
  >;
};

type RegisterUserProfileViewEventAction = {
  eventType: 'UserProfileView';
  input: Omit<
    RegisterVisitorUserProfileViewEventInput,
    'currentUrl' | 'referrerUrl'
  >;
};

/**
 * 🚨 DO NOT EXPORT THIS FROM hooks/index.ts!
 * This is a private hook to be used by useRegisterAnalyticEvent.
 */
export const useSendAnalyticEventMutation = () => {
  const [registerUserProfileViewEvent] =
    useMutation<useSendAnalyticEventMutationProfileViewEventMutation>(graphql`
      mutation useSendAnalyticEventMutationProfileViewEventMutation(
        $input: RegisterVisitorUserProfileViewEventInput!
      ) {
        registerVisitorUserProfileViewEvent(input: $input) {
          registered
        }
      }
    `);

  const [registerProductizedServiceViewEvent] =
    useMutation<useSendAnalyticEventMutationProductizedServiceViewEventMutation>(
      graphql`
        mutation useSendAnalyticEventMutationProductizedServiceViewEventMutation(
          $input: RegisterVisitorProductizedServiceViewEventInput!
        ) {
          registerVisitorProductizedServiceViewEvent(input: $input) {
            registered
          }
        }
      `,
    );

  const [registerPortfolioProjectViewEvent] =
    useMutation<useSendAnalyticEventMutationPortfolioProjectViewEventMutation>(
      graphql`
        mutation useSendAnalyticEventMutationPortfolioProjectViewEventMutation(
          $input: RegisterVisitorPortfolioProjectViewEventInput!
        ) {
          registerVisitorPortfolioProjectViewEvent(input: $input) {
            registered
          }
        }
      `,
    );

  const currentLocation = useLocation();
  const currentUrl = useMemo(() => {
    return [currentLocation.origin, currentLocation.pathname].join('');
  }, [currentLocation]);
  const firstReferrerUrl = useFirstReferrerUrl();

  const registerEvent = useCallback(
    (action: RegisterEventAction) => {
      switch (action.eventType) {
        case 'PortfolioProjectView':
          registerPortfolioProjectViewEvent({
            variables: {
              input: {
                ...action.input,
                currentUrl,
                referrerUrl: firstReferrerUrl,
              },
            },
          });
          break;
        case 'ProductizedServiceView':
          registerProductizedServiceViewEvent({
            variables: {
              input: {
                ...action.input,
                currentUrl,
                referrerUrl: firstReferrerUrl,
              },
            },
          });
          break;
        case 'UserProfileView':
          registerUserProfileViewEvent({
            variables: {
              input: {
                ...action.input,
                currentUrl,
                referrerUrl: firstReferrerUrl,
              },
            },
          });
          break;
      }
    },
    [
      currentUrl,
      firstReferrerUrl,
      registerPortfolioProjectViewEvent,
      registerProductizedServiceViewEvent,
      registerUserProfileViewEvent,
    ],
  );

  return registerEvent;
};
